import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";
import { trackCtaClick } from "@/lib/analytics";
import witkopImg from "@/assets/witkop.webp";

const BookingCTA = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#booking") return;
    // Wait for layout/images so offset is correct; CSS scroll-margin handles navbar offset.
    const id = window.setTimeout(() => {
      document.getElementById("booking")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <section id="booking" className="scroll-mt-28 sm:scroll-mt-32">
      {/* CTA Banner with background image — extra bottom padding clears sticky booking bar + cookie banner */}
      <div className="relative pt-32 pb-40 sm:pb-44">
        <img
          src={witkopImg}
          alt="Witkop with wildebeest at Zwahili"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 85%" }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/20" />

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-4xl tracking-wider text-white mb-4 [text-shadow:_0_2px_12px_rgb(0_0_0_/_0.65)]">
            BEGIN YOUR SAFARI ADVENTURE
          </h2>
          <p className="text-sm text-white/95 mb-8 [text-shadow:_0_1px_8px_rgb(0_0_0_/_0.7)]">
            Book directly with us for the best rates and exclusive benefits.
          </p>
          <a
            href="https://booking.roomraccoon.co.za/zwahili-game-lodge/en/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaClick("Reserve Your Stay", "booking_cta_banner")}
          >
            <Button variant="hero" size="lg" className="text-sm px-12">
              Reserve Your Stay <ArrowRight size={16} />
            </Button>
          </a>
        </div>
      </div>

      {/* Description paragraphs */}
      <div className="bg-background py-4 sm:py-6 lg:py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Exclusive Private Game Lodge accommodation in 3 main lodge rooms, 4 safari suites, 2 executive suites and an opulent presidential suite.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Game viewing in an open Safari Landrover &amp; guided walks by an experienced ranger — see all{" "}
            <Link to="/experiences" className="text-gold underline-offset-4 hover:underline">
              things to do in Groblersdal &amp; Limpopo
            </Link>
            .
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Bird watching with over 300 species of African birdlife with rare sightings such as lizard buzzard and brown snake eagle. Facilities include an{" "}
            <Link to="/spa" className="text-gold underline-offset-4 hover:underline">
              African Spa
            </Link>{" "}
            to revive body, mind &amp; soul, fully licensed restaurant &amp; bar,{" "}
            <Link to="/culinary" className="text-gold underline-offset-4 hover:underline">
              safari boma
            </Link>{" "}
            dinners and{" "}
            <Link to="/culinary" className="text-gold underline-offset-4 hover:underline">
              boma restaurant
            </Link>{" "}
            dining, private in-room dining, swimming pools and a conference facility.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            A maximum of 20 guests are accommodated thereby ensuring a personal and tranquil experience further enhanced by excellent cuisine.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            The lodge caters for exco conferences, weddings and functions to a maximum of 36 people. Zwahili is an ideal honeymoon destination as the lodge exudes an ambiance of splendor while guests experience sophistication of a bygone era.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            With a temperate climate ranging from 15°C to 34°C in the summer months and 8°C to 28°C in the winter, Zwahili is the perfect location all year round.
          </p>
        </div>
      </div>
    </section>
  );
};

export default BookingCTA;
